/**
 * Data layer for `report-export`. Thin typed fetch wrappers over /api/v1/report-export.
 * Raw `fetch()` is confined to this file per the dash contract (rule D6).
 */

export type ReportType =
	| "clicks"
	| "conversions"
	| "payouts"
	| "publisher_performance"
	| "campaign_performance";

export type ExportFormat = "csv" | "xlsx";

export type ExportStatus = "queued" | "running" | "ready" | "failed";

export interface ExportJob {
	id: string;
	tenant_id: string;
	report_type: ReportType;
	format: ExportFormat;
	filters: Record<string, unknown>;
	columns: string[];
	status: ExportStatus;
	rows: number | null;
	download_url: string | null;
	error: string | null;
	created_at: number;
	updated_at: number;
}

export interface ListExportsResponse {
	count: number;
	items: ExportJob[];
}

export interface QueueExportBody {
	report_type: ReportType;
	format: ExportFormat;
	filters: Record<string, unknown>;
	columns: string[];
	date_from: string;
	date_to: string;
}

const BASE = "/api/v1/report-export";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listExports(opts?: { limit?: number }): Promise<ListExportsResponse> {
	const qs = opts?.limit ? `?limit=${encodeURIComponent(String(opts.limit))}` : "";
	const res = await fetch(`${BASE}${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListExportsResponse>(res);
}

export async function queueExport(body: QueueExportBody): Promise<ExportJob> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<ExportJob>(res);
}

export async function downloadExport(id: string): Promise<{ url: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}/download`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ url: string }>(res);
}

// Legacy aliases kept for shared contract compatibility (list/create/remove).
export async function list(): Promise<ListExportsResponse> {
	return listExports();
}

export async function create(body: QueueExportBody): Promise<ExportJob> {
	return queueExport(body);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const reportExportQueryKeys = {
	all: ["report-export"] as const,
	list: () => [...reportExportQueryKeys.all] as const,
	detail: (id: string) => [...reportExportQueryKeys.all, id] as const,
};

export const REPORT_TYPES: { value: ReportType; label: string }[] = [
	{ value: "clicks", label: "Clicks" },
	{ value: "conversions", label: "Conversions" },
	{ value: "payouts", label: "Payouts" },
	{ value: "publisher_performance", label: "Publisher performance" },
	{ value: "campaign_performance", label: "Campaign performance" },
];

export const COLUMN_PRESETS: Record<ReportType, string[]> = {
	clicks: ["click_id", "timestamp", "campaign", "publisher", "country", "device", "landing_page"],
	conversions: ["conversion_id", "timestamp", "campaign", "publisher", "payout", "status", "sub_id"],
	payouts: ["payout_id", "period", "publisher", "gross", "fees", "net", "status"],
	publisher_performance: ["publisher", "clicks", "conversions", "cvr", "revenue", "payout", "margin"],
	campaign_performance: ["campaign", "clicks", "conversions", "cvr", "revenue", "payout", "margin"],
};

export const DEFAULT_COLUMNS: Record<ReportType, string[]> = {
	clicks: ["click_id", "timestamp", "campaign", "publisher", "country"],
	conversions: ["conversion_id", "timestamp", "campaign", "publisher", "payout", "status"],
	payouts: ["payout_id", "period", "publisher", "net", "status"],
	publisher_performance: ["publisher", "clicks", "conversions", "cvr", "revenue"],
	campaign_performance: ["campaign", "clicks", "conversions", "cvr", "revenue"],
};
